import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import './App.css';

// Data
import { translations, availableLanguages } from './data/translations';

// Components
import Header from './components/Header';
import Navbar from './components/Navbar';
import Footer from './components/Footer';

// Pages
import Calendar from './pages/Calendar';
import Standings from './pages/Standings';
import PointsSystem from './pages/PointsSystem';
import Teams from './pages/Teams';
import Simulator from './pages/Simulator';

function App() {
  const [userTimezone, setUserTimezone] = useState('');
  const [language, setLanguage] = useState('en');
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    // Detectar la zona horaria del usuario con offset
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const now = new Date();
    const offset = -now.getTimezoneOffset() / 60;
    const offsetStr = offset >= 0 ? `+${offset}` : `${offset}`;
    setUserTimezone(`${timezone} (UTC${offsetStr})`);

    // Detectar idioma desde URL query param (?lang=es)
    const params = new URLSearchParams(window.location.search);
    const urlLang = params.get('lang');
    if (urlLang && availableLanguages.some(lang => lang.code === urlLang)) {
      setLanguage(urlLang);
    } else {
      // Detectar idioma del navegador
      const browserLang = navigator.language.split('-')[0]; // 'es-AR' -> 'es'
      const supportedLang = availableLanguages.find(lang => lang.code === browserLang);
      setLanguage(supportedLang ? browserLang : 'en');
    }
  }, []);

  // Obtener traducciones actuales
  const t = translations[language];

  // SEO meta tags optimizados para cada idioma
  const seoContent = {
    en: {
      title: "F1 Calendar 2026 & Countdown | Race Schedule & Times",
      description: "Live countdown to the next F1 race 2026. Complete calendar with race times in your timezone, drivers standings, teams & points table. Track the entire Formula 1 season.",
      keywords: "F1 calendar 2026, Formula 1 schedule, F1 countdown, F1 race times, F1 drivers, F1 teams, F1 points system, Grand Prix calendar, F1 season tracker"
    },
    es: {
      title: "Calendario F1 2026 y Cuenta Regresiva | Horarios y Carreras",
      description: "Cuenta atrás en vivo para la próxima carrera de F1 2026. Calendario completo con horarios en tu zona, clasificación de pilotos, escuderías y tabla de puntos. Sigue toda la temporada de Fórmula 1.",
      keywords: "calendario F1 2026, horarios Fórmula 1, cuenta regresiva F1, carreras F1, pilotos F1, escuderías F1, sistema de puntos F1, calendario Gran Premio, temporada F1, pretemporada F1"
    },
    zh: {
      title: "F1赛历2026与倒计时 | 比赛时间表",
      description: "F1 2026下一场比赛实时倒计时。完整赛历，您时区的比赛时间，车手积分榜，车队和积分系统。追踪整个一级方程式赛季。",
      keywords: "F1赛历2026, 一级方程式时间表, F1倒计时, F1比赛时间, F1车手, F1车队, F1积分系统, 大奖赛赛历"
    },
    ja: {
      title: "F1カレンダー2026とカウントダウン | レーススケジュール",
      description: "F1 2026次のレースへのライブカウントダウン。あなたのタイムゾーンでのレース時間、ドライバーランキング、チームとポイント表を含む完全なカレンダー。F1シーズン全体を追跡。",
      keywords: "F1カレンダー2026, フォーミュラ1スケジュール, F1カウントダウン, F1レース時間, F1ドライバー, F1チーム, F1ポイントシステム"
    },
    ar: {
      title: "تقويم الفورمولا 1 2026 والعد التنازلي | جدول السباقات",
      description: "العد التنازلي المباشر لسباق F1 2026 القادم. تقويم كامل مع أوقات السباق في منطقتك الزمنية، ترتيب السائقين، الفرق وجدول النقاط. تتبع موسم الفورمولا 1 بالكامل.",
      keywords: "تقويم F1 2026, جدول الفورمولا 1, العد التنازلي F1, أوقات سباق F1, سائقو F1, فرق F1, نظام نقاط F1"
    },
    fr: {
      title: "Calendrier F1 2026 et Compte à Rebours | Horaires des Courses",
      description: "Compte à rebours en direct pour la prochaine course F1 2026. Calendrier complet avec horaires dans votre fuseau horaire, classement des pilotes, équipes et tableau des points. Suivez toute la saison de Formule 1.",
      keywords: "calendrier F1 2026, horaires Formule 1, compte à rebours F1, courses F1, pilotes F1, équipes F1, système de points F1, calendrier Grand Prix"
    },
    it: {
      title: "Calendario F1 2026 e Conto alla Rovescia | Orari Gare",
      description: "Conto alla rovescia in diretta per la prossima gara F1 2026. Calendario completo con orari nel tuo fuso orario, classifica piloti, team e tabella punti. Segui l'intera stagione di Formula 1.",
      keywords: "calendario F1 2026, orari Formula 1, conto alla rovescia F1, gare F1, piloti F1, team F1, sistema punti F1, calendario Gran Premio"
    },
    nl: {
      title: "F1 Kalender 2026 en Aftelling | Raceschema en Tijden",
      description: "Live aftelling naar de volgende F1-race 2026. Volledige kalender met racetijden in jouw tijdzone, coureurs klassement, teams en puntentabel. Volg het hele Formule 1-seizoen.",
      keywords: "F1 kalender 2026, Formule 1 schema, F1 aftelling, F1 racetijden, F1 coureurs, F1 teams, F1 puntensysteem, Grand Prix kalender"
    },
    hu: {
      title: "F1 Naptár 2026 és Visszaszámlálás | Versenyidőpontok",
      description: "Élő visszaszámlálás a következő F1 versenyig 2026. Teljes naptár az Ön időzónájában, versenyzői rangsor, csapatok és pontrendszer. Kövesse az egész Forma-1 szezont.",
      keywords: "F1 naptár 2026, Forma-1 menetrend, F1 visszaszámlálás, F1 versenyidők, F1 versenyzők, F1 csapatok, F1 pontrendszer, Nagydíj naptár"
    },
    pt: {
      title: "Calendário F1 2026 e Contagem Regressiva | Horários das Corridas",
      description: "Contagem regressiva ao vivo para a próxima corrida F1 2026. Calendário completo com horários no seu fuso horário, classificação de pilotos, equipes e tabela de pontos. Acompanhe toda a temporada de Fórmula 1.",
      keywords: "calendário F1 2026, horários Fórmula 1, contagem regressiva F1, corridas F1, pilotos F1, equipes F1, sistema de pontos F1, calendário Grande Prêmio"
    },
    de: {
      title: "F1-Kalender 2026 & Countdown | Rennkalender & Sendezeiten",
      description: "Live-Countdown zum nächsten F1-Rennen 2026. Kompletter Rennkalender mit Zeiten in Ihrer Zeitzone, Fahrerwertung, Teams & WM-Stand. Verfolge die Formel 1.",
      keywords: "F1 Kalender 2026, Formel 1 Rennkalender, F1 Countdown, F1 Rennzeiten, F1 Fahrer, F1 Teams, F1 Punktesystem, Großer Preis Kalender"
    }
  };

  const currentSEO = seoContent[language] || seoContent.en;

  return (
    <BrowserRouter>
      <div className="app-container">
        <Helmet>
          <html lang={language} />
          <title>{currentSEO.title}</title>
          <meta name="description" content={currentSEO.description} />
          <meta name="keywords" content={currentSEO.keywords} />

          {/* Open Graph / Facebook */}
          <meta property="og:type" content="website" />
          <meta property="og:title" content={currentSEO.title} />
          <meta property="og:description" content={currentSEO.description} />

          {/* Twitter */}
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:title" content={currentSEO.title} />
          <meta name="twitter:description" content={currentSEO.description} />

          {/* Additional SEO */}
          <meta name="robots" content="index, follow" />
          <meta name="author" content="Marcelo Reyes" />
          <link rel="canonical" href={window.location.href} />
        </Helmet>

        {/* SIDEBAR */}
        <Navbar t={t} sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />

        {/* MAIN CONTENT */}
        <div className="main-content">
          <Header
            t={t}
            language={language}
            setLanguage={setLanguage}
            userTimezone={userTimezone}
            setSidebarOpen={setSidebarOpen}
          />

          <main>
            <Routes>
              <Route path="/" element={<Calendar t={t} />} />
              <Route path="/standings" element={<Standings t={t} language={language} />} />
              <Route path="/points" element={<PointsSystem t={t} />} />
              <Route path="/teams" element={<Teams t={t} />} />
              <Route path="/simulator" element={<Simulator t={t} language={language} />} />
              <Route path="/cars" element={<Navigate to="/teams" replace />} />
              <Route path="*" element={<Navigate to="/" />} />
            </Routes>

            <Footer t={t} />
          </main>
        </div>
      </div>
    </BrowserRouter>
  );
}

export default App;